"use client";
import { useForm, FieldValues } from "react-hook-form";
import { useEffect } from "react";
import { Field } from "../dynamic/DynamicForm";
import DynamicInput from "../dynamic/DynamicInput";
import { useTranslation } from "react-i18next";
import { formatDateTime, trimStrings } from "services/helper";
import Button from "components/button/Button";
import { FiRefreshCw } from "react-icons/fi";
import { FaSearch } from "react-icons/fa";

type DynamicFilterProps = {
  onSubmit: (formData: FieldValues) => void | Promise<void>;
  fields: Field[];
  defaultValues?: FieldValues;
  isEdit?: boolean;
  loading?: boolean;
  isMobile?: boolean;
  onClose?: () => void;
};

export default function DynamicFilter({
  onSubmit,
  fields,
  defaultValues = {},
  isEdit,
  loading,
  isMobile,
  onClose,
}: DynamicFilterProps) {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { dirtyFields },
  } = useForm({ defaultValues });

  useEffect(() => {
    const fixedDefaults = { ...defaultValues };
    fields.forEach((field) => {
      const val = fixedDefaults[field.name];
      if (val === undefined || val === null) {
        if (field.type === "date" || field.type === "time")
          fixedDefaults[field.name] = null;
        else if (field.type === "select" && field.multiple)
          fixedDefaults[field.name] = [];
        else fixedDefaults[field.name] = "";
      } else {
        if (
          (field.type === "date" || field.type === "time") &&
          typeof val === "string"
        ) {
          const d = new Date(val);
          fixedDefaults[field.name] = isNaN(d.getTime()) ? null : d;
        }
      }
    });

    reset(fixedDefaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  type FormResult = Record<string, unknown>;

  const submitHandler = (data: FieldValues) => {
    const trimmedData = trimStrings(data);
    const formattedData = formatDateTime(trimmedData, fields);

    const result: FormResult = isEdit
      ? (Object.keys(dirtyFields) as Array<keyof typeof formattedData>).reduce(
          (acc, key) => {
            acc[key] = formattedData[key];
            return acc;
          },
          {} as FormResult
        )
      : formattedData;

    onSubmit(result);

    if (isMobile && onClose) {
      onClose();
    }
  };

  return (
    <form
      onSubmit={handleSubmit(submitHandler)}
      noValidate
      className={isMobile ? "block" : "hidden md:block"}
    >
      <div
        className={`
        custom-scrollbar grid gap-1 p-3 md:p-1
        ${
          isMobile
            ? "max-h-[50vh] grid-cols-1 overflow-y-auto"
            : "max-h-[450px] grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5"
        }
      `}
      >
        <DynamicInput
          fields={fields}
          control={control}
          setValue={setValue}
          filter={true}
        />
        <div className="mt-2 flex w-full items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => reset()}
            startIcon={<FiRefreshCw size={16} />}
            className="w-full"
          >
            {t("buttons.reset")}
          </Button>

          <Button
            type="submit"
            variant="info"
            size="sm"
            loading={loading}
            startIcon={<FaSearch size={16} />}
            className="w-full"
          >
            {t("buttons.search")}
          </Button>
        </div>
      </div>
    </form>
  );
}
